import _ from "lodash";
import moment from "moment";
import {
  formatDate,
  checkIsDateSame,
  checkIsDateBefore,
} from "Utils/DateTimeHelpers";
import { PRIVATE, VISIBLE } from "Constants/stringConstants";

/** Date format used in summary text display */
const SUMMARY_DATE_FORMAT = "DD MMM YYYY [at] hh:mm A";

// * Formats a duration object into a human-readable string with proper pluralization
const formatDuration = ({ hours, minutes }, t) => {
  const withHour = hr => t("common:hour_count_other", { count: hr });
  const withMinute = min => t("common:count_minute_other", { count: min });
  if (hours > 0 && minutes > 0) {
    return `${withHour(hours)} ${withMinute(minutes)}`;
  } else {
    return hours > 0 ? `${withHour(hours)}` : `${withMinute(minutes)}`;
  }
};

// * Determines the relationship between close and due dates for summary generation
const getCloseVsDueDateRelation = ({ closesOn, dueOn }) => {
  // Return null if either date is missing
  if (_.isEmpty(closesOn) || _.isEmpty(dueOn)) return null;
  // Check if close and due dates are identical (minute-level precision)
  if (checkIsDateSame({ date1: closesOn, date2: dueOn, check: "minute" })) {
    return "same";
  }
  // Close date occurs after due date
  return "after";
};

// * Deadline Summary Text
const getDeadlineSummary = ({
  t,
  tokens,
  hasDueDate,
  closeVsDue,
  hasCloseDate,
}) => {
  // No deadlines configured
  if (!hasDueDate && !hasCloseDate) return "";
  // Only close date configured (no late submission allowed)
  if (!hasDueDate && hasCloseDate) {
    return t("worksheet:summary_close_no_late", tokens);
  }
  // Only due date configured (late submission allowed)
  if (hasDueDate && !hasCloseDate) {
    return t("worksheet:summary_due_late", tokens);
  }
  // Both dates exist: check if they are the same time
  if (closeVsDue === "same") {
    return t("worksheet:summary_due_close_same", tokens);
  }
  // Both dates exist and close is after due
  return t("worksheet:summary_due_close_after", tokens);
};

// * No Deadline Text
const getNoDeadlineText = ({ isTimedTestEnabled, t }) => {
  // Timed tests reference "due date"; untimed worksheets reference "submission deadline"
  return isTimedTestEnabled
    ? t("worksheet:summary_no_due_date")
    : t("worksheet:summary_no_submission_deadline");
};

const getDate = token => {
  if (_.isEmpty(token)) return "";
  return formatDate({ date: token, format: SUMMARY_DATE_FORMAT });
};

/**
 * Generates a complete localized summary text for worksheet assignment.
 * Handles three worksheet types (fixed time, timed, untimed) and generates appropriate
 * messaging based on visibility, open dates, deadlines, and duration.
 * @param {Object} options - The configuration for generating summary
 * @returns {string} Localized summary text describing the worksheet assignment configuration
 */
export const getWorksheetAssignSummaryText = ({
  t,
  dueOn,
  opensOn,
  closesOn,
  visibility,
  isFixedStartTime,
  timedTestDuration,
  isTimedTestEnabled,
}) => {
  const tokens = {
    openDate: getDate(opensOn),
    startDate: getDate(opensOn),
    dueDate: getDate(dueOn),
    closeDate: getDate(closesOn),
    duration: timedTestDuration ? formatDuration(timedTestDuration, t) : "",
  };

  // Private worksheets show no information
  if (visibility === PRIVATE) return t("worksheet:summary_private_no_info");

  // Check if worksheet is currently open (no open date or open date has passed)
  const isOpenNow =
    _.isEmpty(opensOn) ||
    checkIsDateBefore({ date1: opensOn, date2: moment(), key: "minutes" });

  // Determine deadline configuration
  const hasDueDate = !_.isEmpty(dueOn);
  const hasCloseDate = !_.isEmpty(closesOn);
  const closeVsDue = getCloseVsDueDateRelation({ closesOn, dueOn });

  // Prepare parameters for deadline summary generation
  const deadlineParams = { hasDueDate, hasCloseDate, closeVsDue, t, tokens };
  // Array to accumulate summary text parts (joined with spaces at the end)
  const parts = [];

  // * Fixed Time Worksheet
  if (isFixedStartTime) {
    if (isOpenNow) {
      // Worksheet is currently active
      parts.push(t("worksheet:summary_intro_fixed_now", tokens));
    } else if (visibility === VISIBLE) {
      // Future worksheet but visibility is public - show when it opens
      parts.push(t("worksheet:summary_intro_fixed_future_visible", tokens));
      parts.push(t("worksheet:summary_duration_fixed", tokens));
    } else {
      // Future worksheet with hidden visibility - only show info once it opens
      parts.push(t("worksheet:summary_intro_fixed_future_on_start", tokens));
      parts.push(t("worksheet:summary_duration", tokens));
    }
    return parts.join(" ");
  }

  // * Flexible Time Worksheet
  if (isTimedTestEnabled) {
    if (isOpenNow) {
      // Worksheet is currently available with duration
      parts.push(t("worksheet:summary_intro_open_now_with_duration", tokens));
    } else if (visibility === VISIBLE) {
      // Future worksheet but visibility is public - show when it opens
      parts.push(t("worksheet:summary_intro_future_visible", tokens));
      parts.push(t("worksheet:summary_duration", tokens));
    } else {
      // Future worksheet with hidden visibility - only show info once it opens
      parts.push(t("worksheet:summary_intro_future_on_open", tokens));
      parts.push(t("worksheet:summary_duration", tokens));
    }

    // Add deadline information if available
    const deadline = getDeadlineSummary(deadlineParams);
    if (deadline) {
      parts.push(deadline);
    } else if (!hasDueDate && !hasCloseDate) {
      parts.push(getNoDeadlineText({ isTimedTestEnabled, t }));
    }
    return parts.join(" ");
  }

  // * Untimed Worksheet
  // An untimed worksheet has no time limit and allows students to work at their own pace
  if (isOpenNow) {
    parts.push(t("worksheet:summary_intro_open_now", tokens));
  } else if (visibility === VISIBLE) {
    parts.push(t("worksheet:summary_intro_future_visible", tokens));
  } else {
    parts.push(t("worksheet:summary_intro_future_on_open", tokens));
  }

  // Add deadline information if available
  const deadline = getDeadlineSummary(deadlineParams);
  if (deadline) {
    parts.push(deadline);
  } else if (!hasDueDate && !hasCloseDate) {
    parts.push(getNoDeadlineText({ isTimedTestEnabled, t }));
  }
  return _.join(parts, " ");
};
